import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { locationsService } from '../services/locationsService';
import apiClient from '../services/api';

const RestaurantContext = createContext();

export const useRestaurant = () => {
  const context = useContext(RestaurantContext);
  if (!context) {
    throw new Error('useRestaurant must be used within RestaurantProvider');
  }
  return context;
};

export const RestaurantProvider = ({ children }) => {
  const { user, isSuperAdmin } = useAuth();
  const [restaurantId, setRestaurantId] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  // The location the user picked at login (implicit multi-location access).
  const [locationId, setLocationId] = useState(() => {
    const v = localStorage.getItem('locationId');
    return v ? parseInt(v, 10) : null;
  });

  // Resolve the active restaurantId against accessible companies, self-healing a stale localStorage value.
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;

    const userRestaurantId = user?.restaurant_id || user?.restaurant?.id || user?.restaurant || null;
    const storedRaw = localStorage.getItem('restaurantId') || sessionStorage.getItem('restaurantId');
    const stored = storedRaw ? parseInt(storedRaw, 10) : null;

    const apply = (id) => {
      if (id == null) return;
      setRestaurantId(id);
      localStorage.setItem('restaurantId', String(id));
    };

    (async () => {
      let accessible = [];
      try {
        const { data } = await apiClient.get('/users/restaurants/');
        if (data?.success) accessible = data.data || [];
      } catch (_) {
        // On a network failure fall back to the best local guess rather than wiping the current selection.
        if (!cancelled) apply(userRestaurantId || stored);
        return;
      }
      if (cancelled) return;

      const ids = new Set(accessible.map((r) => r.id));
      let resolved = null;
      if (userRestaurantId && ids.has(userRestaurantId)) {
        resolved = userRestaurantId;            // own company, still valid
      } else if (stored && ids.has(stored)) {
        resolved = stored;                      // last pick, still valid
      } else if (accessible.length) {
        resolved = accessible[0].id;            // stale/deleted id → first valid one
      } else {
        resolved = userRestaurantId || stored;  // nothing returned; keep local guess
      }
      apply(resolved);
      // Expose the active company object (for its name in the header, etc.).
      const obj = accessible.find((r) => r.id === resolved) || null;
      if (obj) setRestaurant(obj);
    })();

    return () => { cancelled = true; };
  }, [user, isSuperAdmin]);

  const setActiveRestaurant = (id) => {
    setRestaurantId(id);
    localStorage.setItem('restaurantId', id);
  };

  const setActiveLocation = (id) => {
    setLocationId(id);
    if (id == null) {
      localStorage.removeItem('locationId');
    } else {
      localStorage.setItem('locationId', String(id));
    }
  };

  // Resolve the active location, defaulting to the primary one so the app always has a location.
  const [location, setLocation] = useState(null);
  useEffect(() => {
    if (!restaurantId) {
      setLocation(null);
      return undefined;
    }
    let cancelled = false;
    locationsService.list(restaurantId)
      .then((locs) => {
        if (cancelled) return;
        const active = (locs || []).filter((l) => l.is_active !== false);
        let chosen = locationId ? active.find((l) => l.id === locationId) : null;
        if (!chosen) {
          chosen = active.find((l) => l.is_primary) || active[0] || null;
          if (chosen && chosen.id !== locationId) {
            setLocationId(chosen.id);
            localStorage.setItem('locationId', String(chosen.id));
          }
        }
        setLocation(chosen || null);
      })
      .catch(() => { if (!cancelled) setLocation(null); });
    return () => { cancelled = true; };
  }, [restaurantId, locationId]);

  return (
    <RestaurantContext.Provider value={{
      restaurantId,
      setRestaurantId: setActiveRestaurant,
      restaurant,
      setRestaurant,
      locationId,
      setLocationId: setActiveLocation,
      location,
    }}>
      {children}
    </RestaurantContext.Provider>
  );
};
